import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import ForceGraph2D from 'react-force-graph-2d';
import type { AccumulatedGraph, AccumulatedNode, StoredMessage } from '../../types';
import { SubgraphToolbar } from './SubgraphToolbar';
import { NodeDetailsPanel } from './NodeDetailsPanel';
import { GraphLegendPanel } from './GraphLegendPanel';
import { expandNeighbors } from '../../lib/graphApi';
import { nodeColor } from '../../lib/entityColors';
import { classifyIOC, type IOCType } from '../../lib/iocClassifier';
import { serializeGraphToPrompt } from '../../lib/graphSerializer';
import {
  useForceGraph,
  edgeColor,
  sourceEdgeColor,
  resolveKnowledgeSource,
  KNOWLEDGE_SOURCE_LABELS,
  type EdgeColorMode,
  type GraphNode,
  type GraphLink,
  type FGNode,
  type FGLink,
  type FGRef,
} from './useForceGraph';

interface TooltipState {
  x: number;
  y: number;
  name: string;
  type: string;
  strategy?: string;
  score?: number;
  neighborCount?: number;
  neighborSummary?: string;
}

interface AccumulatedSubgraphPanelProps {
  graph: AccumulatedGraph;
  onTogglePin: (nodeId: string) => void;
  onClearUnpinned: () => void;
  onNeighborExpansion: (centerNodeId: string, nodes: any[], edges: any[]) => void;
  onNodePositionChange: (nodeId: string, pos: { x: number; y: number }) => void;
  subgraphScope: 'none' | 'search_within' | 'expand_from';
  onSetScope: (scope: 'none' | 'search_within' | 'expand_from') => void;
  messages: StoredMessage[];
  emptyMessage?: string;
  onClearAll?: () => void;
  onExpand?: () => void;
  expandLabel?: string;
  expandTitle?: string;
  onSaveToGraphs?: () => void;
}

function buildNeighborSummary(nodeId: string, graph: AccumulatedGraph) {
  const labelCounts: Record<string, number> = {};
  for (const e of graph.edges) {
    const neighborId = e.source === nodeId ? e.target : e.target === nodeId ? e.source : null;
    if (!neighborId) continue;
    const neighbor = graph.nodes.find(n => n.id === neighborId);
    if (neighbor) {
      labelCounts[neighbor.label] = (labelCounts[neighbor.label] || 0) + 1;
    }
  }
  const neighborCount = Object.values(labelCounts).reduce((a, b) => a + b, 0);
  const SHORT: Record<string, string> = {
    Document: 'doc', MalwareFamily: 'family', ThreatActor: 'actor',
    IOC: 'IOC', CVE: 'CVE', AttackTechnique: 'technique', WebSource: 'web',
  };
  const neighborSummary = Object.entries(labelCounts)
    .sort((a, b) => b[1] - a[1])
    .map(([lbl, cnt]) => `${cnt} ${SHORT[lbl] || lbl}${cnt > 1 ? 's' : ''}`)
    .join(', ');
  return { neighborCount, neighborSummary };
}

export function AccumulatedSubgraphPanel({
  graph,
  onTogglePin,
  onClearUnpinned,
  onNeighborExpansion,
  onNodePositionChange,
  subgraphScope,
  onSetScope,
  messages,
  emptyMessage,
  onClearAll,
  onExpand,
  expandLabel,
  expandTitle,
  onSaveToGraphs,
}: AccumulatedSubgraphPanelProps) {
  const navigate = useNavigate();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [selectedNode, setSelectedNode] = useState<AccumulatedNode | null>(null);
  const [typeFilters, setTypeFilters] = useState<Record<string, boolean>>({});
  const [iocSubTypeFilters, setIocSubTypeFilters] = useState<Record<IOCType, boolean>>({
    HASH: true, IP: true, DOMAIN: true, URL: true, EMAIL: true, UNKNOWN: true,
  });
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; nodeId?: string; isCanvas: boolean } | null>(null);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const [dimensions, setDimensions] = useState({ width: 400, height: 360 });

  const ALL_TYPES = ['MalwareFamily', 'ThreatActor', 'Document', 'CVE', 'IOC', 'AttackTechnique', 'WebSource'];
  const [edgeColorMode, setEdgeColorMode] = useState<EdgeColorMode>('source');

  const handleToggleTypeFilter = useCallback((type: string) => {
    setTypeFilters(prev => ({ ...prev, [type]: prev[type] === false ? true : false }));
  }, []);

  const handleToggleIocSubType = useCallback((type: IOCType) => {
    setIocSubTypeFilters(prev => ({ ...prev, [type]: prev[type] === false ? true : false }));
  }, []);

  const handleDateRangeChange = useCallback((from: string, to: string) => {
    setFilterDateFrom(from);
    setFilterDateTo(to);
  }, []);

  const handleShowAll = useCallback(() => {
    const all: Record<string, boolean> = {};
    ALL_TYPES.forEach(t => { all[t] = true; });
    setTypeFilters(all);
    setIocSubTypeFilters({ HASH: true, IP: true, DOMAIN: true, URL: true, EMAIL: true, UNKNOWN: true });
  }, []);

  const handleHideAll = useCallback(() => {
    const all: Record<string, boolean> = {};
    ALL_TYPES.forEach(t => { all[t] = false; });
    setTypeFilters(all);
  }, []);

  // ── Transform data ─────────────────────────────────────────────────────

  const graphData = useMemo(() => {
    // Pass 1: type + IOC sub-type + date filtering
    let filteredNodes = graph.nodes.filter(n => {
      if (typeFilters[n.label] === false) return false;
      if (n.label === 'IOC' && iocSubTypeFilters[classifyIOC(n.name)] === false) return false;
      if (n.label === 'Document' && (filterDateFrom || filterDateTo)) {
        const docDate = (n.properties?.date as string) || '';
        if (docDate) {
          if (filterDateFrom && docDate < filterDateFrom) return false;
          if (filterDateTo && docDate > filterDateTo) return false;
        } else {
          return false;
        }
      }
      return true;
    });

    // Pass 2: remove orphaned nodes when date filter is active
    if (filterDateFrom || filterDateTo) {
      const visibleIds = new Set(filteredNodes.map(n => n.id));
      const connectedToVisible = new Set<string>();
      for (const e of graph.edges) {
        if (visibleIds.has(e.source) && visibleIds.has(e.target)) {
          connectedToVisible.add(e.source);
          connectedToVisible.add(e.target);
        }
      }
      filteredNodes = filteredNodes.filter(n => {
        if (n.label === 'Document') return true;
        const hasAnyEdge = graph.edges.some(e => e.source === n.id || e.target === n.id);
        if (!hasAnyEdge) return true;
        return connectedToVisible.has(n.id);
      });
    }

    const filteredNodeIds = new Set(filteredNodes.map(n => n.id));
    const filteredEdges = graph.edges.filter(e => filteredNodeIds.has(e.source) && filteredNodeIds.has(e.target));

    const nodes: FGNode[] = filteredNodes.map(n => {
      const color = nodeColor(n.label, n.name);
      const isSmall = n.label === 'Document' || n.label === 'IOC' || n.label === 'CVE';
      return {
        id: n.id,
        name: n.name,
        label: n.label,
        color,
        size: isSmall ? 3 : 5,
        isSeed: false,
        pinned: n.pinned,
        age: graph.queryCount - n.lastSeenAtQueryIndex,
        strategy: n.strategy,
        score: n.score,
        fx: n.pinned ? n.position?.x : undefined,
        fy: n.pinned ? n.position?.y : undefined,
      };
    });

    const links: FGLink[] = filteredEdges.map(e => {
      const ec = edgeColorMode === 'source'
        ? sourceEdgeColor(e.rel_type, e.data_source)
        : edgeColor(e.rel_type);
      return {
        source: e.source,
        target: e.target,
        rel_type: e.rel_type,
        color: ec.line,
        particleColor: ec.particle,
        expanded: e.expanded,
        data_source: e.data_source,
      };
    });

    return { nodes, links };
  }, [graph, typeFilters, iocSubTypeFilters, filterDateFrom, filterDateTo, edgeColorMode]);

  // ── Force graph hook ───────────────────────────────────────────────────

  const {
    fgRef,
    nodeCanvasObject,
    nodePointerAreaPaint,
    handleNodeHover,
    handleNodeUnhover,
    handleNodeClick,
    linkColor,
    linkWidth,
    linkParticleColor,
    linkLineDash,
    focusNode,
    zoomToFit,
  } = useForceGraph({
    onNodeClick: (node) => {
      const accNode = graph.nodes.find(n => n.id === node.id);
      if (accNode) setSelectedNode(accNode);
      setContextMenu(null);
    },
    onNodeRightClick: (node, event) => {
      event.preventDefault();
      setContextMenu({ x: event.offsetX, y: event.offsetY, nodeId: node.id as string, isCanvas: false });
    },
    onBackgroundClick: () => {
      setSelectedNode(null);
      setContextMenu(null);
    },
    onBackgroundRightClick: (event) => {
      event.preventDefault();
      setContextMenu({ x: event.offsetX, y: event.offsetY, isCanvas: true });
    },
    highlightedNodeId: selectedNode?.id ?? null,
  });

  // ── Tooltip ────────────────────────────────────────────────────────────

  const onNodeHover = useCallback((node: FGNode | null, prev: FGNode | null) => {
    handleNodeHover(node, prev, graphData);

    if (!node) {
      setTooltip(null);
      return;
    }

    const fg = fgRef.current;
    if (!fg) return;
    const screenPos = fg.graph2ScreenCoords(node.x ?? 0, node.y ?? 0);
    const { neighborCount, neighborSummary } = buildNeighborSummary(node.id as string, graph);

    setTooltip({
      x: screenPos.x,
      y: screenPos.y,
      name: (node as GraphNode).name,
      type: (node as GraphNode).label,
      strategy: (node as GraphNode).strategy,
      score: (node as GraphNode).score,
      neighborCount,
      neighborSummary,
    });
  }, [handleNodeHover, graphData, graph, fgRef]);

  const onLinkHover = useCallback((link: FGLink | null) => {
    if (!link) { setTooltip(null); return; }
    const fg = fgRef.current;
    if (!fg) return;
    const src = link.source as FGNode;
    const tgt = link.target as FGNode;
    if (!src?.x || !tgt?.x) return;
    const mx = ((src.x ?? 0) + (tgt.x ?? 0)) / 2;
    const my = ((src.y ?? 0) + (tgt.y ?? 0)) / 2;
    const screenPos = fg.graph2ScreenCoords(mx, my);
    const gl = link as GraphLink;
    const ks = resolveKnowledgeSource(gl.rel_type, gl.data_source);
    setTooltip({ x: screenPos.x, y: screenPos.y, name: gl.rel_type || 'related', type: KNOWLEDGE_SOURCE_LABELS[ks] });
  }, [fgRef]);

  // ── Drag end: pin + persist position ───────────────────────────────────

  const onNodeDragEnd = useCallback((node: FGNode) => {
    node.fx = node.x;
    node.fy = node.y;
    onNodePositionChange(node.id as string, { x: node.x ?? 0, y: node.y ?? 0 });
  }, [onNodePositionChange]);

  // ── Container resize ───────────────────────────────────────────────────

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const obs = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) setDimensions({ width, height });
      }
    });
    obs.observe(wrapper);
    return () => obs.disconnect();
  }, []);

  // Zoom to fit on data changes
  useEffect(() => {
    if (graphData.nodes.length > 0) {
      const t = setTimeout(() => zoomToFit(30, 400), 200);
      return () => clearTimeout(t);
    }
  }, [graph.lastUpdated]);

  // ── Configure forces after mount ─────────────────────────────────────

  useEffect(() => {
    const fg = fgRef.current;
    if (!fg) return;
    const charge = fg.d3Force('charge');
    if (charge && typeof (charge as any).strength === 'function') {
      (charge as any).strength(-80);
    }
    const link = fg.d3Force('link');
    if (link && typeof (link as any).distance === 'function') {
      (link as any).distance(50);
    }
  });

  // ── Reset layout ──────────────────────────────────────────────────────

  const resetLayout = useCallback(() => {
    for (const node of graphData.nodes) {
      const accNode = graph.nodes.find(n => n.id === node.id);
      if (!accNode?.pinned) {
        node.fx = undefined;
        node.fy = undefined;
      }
    }
    fgRef.current?.d3ReheatSimulation();
    setTimeout(() => zoomToFit(30, 400), 400);
  }, [graphData.nodes, graph.nodes, fgRef, zoomToFit]);

  // ── Context menu actions ───────────────────────────────────────────────

  const handleContextAction = useCallback(async (action: string) => {
    const nodeId = contextMenu?.nodeId;
    setContextMenu(null);

    if (action === 'pin' && nodeId) {
      onTogglePin(nodeId);
    } else if (action === 'expand' && nodeId) {
      const accNode = graph.nodes.find(n => n.id === nodeId);
      if (!accNode) return;
      try {
        const resp = await expandNeighbors(nodeId, accNode.label, accNode.name);
        onNeighborExpansion(nodeId, resp.nodes, resp.edges);
      } catch (e) {
        console.warn('Neighbor expansion failed:', e);
      }
    } else if (action === 'copy' && nodeId) {
      const accNode = graph.nodes.find(n => n.id === nodeId);
      if (accNode) navigator.clipboard.writeText(accNode.name);
    } else if (action === 'inspect' && nodeId) {
      const accNode = graph.nodes.find(n => n.id === nodeId);
      if (accNode) setSelectedNode(accNode);
    } else if (action === 'search_within') {
      onSetScope('search_within');
    } else if (action === 'expand_from') {
      onSetScope('expand_from');
    } else if (action === 'reset_layout') {
      resetLayout();
    } else if (action === 'go_to_document' && nodeId) {
      const accNode = graph.nodes.find(n => n.id === nodeId);
      if (!accNode || accNode.label !== 'Document') return;
      if (accNode.name.startsWith('web:')) {
        window.open(accNode.name.replace(/^web:/, 'https://'), '_blank', 'noopener,noreferrer');
      } else {
        navigate(`/sources/${encodeURIComponent(accNode.name)}`);
      }
    } else if (action === 'clear_unpinned') {
      onClearUnpinned();
    }
  }, [contextMenu, graph.nodes, onTogglePin, onNeighborExpansion, onClearUnpinned, onSetScope, resetLayout, navigate]);

  const handleFocusNode = useCallback((nodeId: string) => {
    focusNode(nodeId, graphData);
    const accNode = graph.nodes.find(n => n.id === nodeId);
    if (accNode) setSelectedNode(accNode);
  }, [focusNode, graphData, graph.nodes]);

  const pinnedNode = contextMenu?.nodeId ? graph.nodes.find(n => n.id === contextMenu.nodeId) : null;

  const { visibleNodes, visibleEdges, hasActiveFilters } = useMemo(() => {
    const active = Object.values(typeFilters).some(v => v === false)
      || Object.values(iocSubTypeFilters).some(v => v === false)
      || !!filterDateFrom || !!filterDateTo;
    return {
      visibleNodes: graphData.nodes.length,
      visibleEdges: graphData.links.length,
      hasActiveFilters: active,
    };
  }, [graphData, typeFilters, iocSubTypeFilters, filterDateFrom, filterDateTo]);

  const handleCopyGraphToClipboard = useCallback(() => {
    const visibleNodeIds = new Set(graphData.nodes.map(n => n.id));
    const accNodes = graph.nodes.filter(n => visibleNodeIds.has(n.id));
    const accEdges = graph.edges.filter(e => visibleNodeIds.has(e.source) && visibleNodeIds.has(e.target));
    const text = serializeGraphToPrompt(accNodes, accEdges, {
      title: 'Investigation Subgraph',
    });
    navigator.clipboard.writeText(text);
    toast.success('Graph context copied to clipboard');
  }, [graphData.nodes, graph]);

  const handleInsertGraphIntoChat = useCallback(() => {
    const visibleNodeIds = new Set(graphData.nodes.map(n => n.id));
    const accNodes = graph.nodes.filter(n => visibleNodeIds.has(n.id));
    const accEdges = graph.edges.filter(e => visibleNodeIds.has(e.source) && visibleNodeIds.has(e.target));
    const text = serializeGraphToPrompt(accNodes, accEdges, {
      title: 'Investigation Subgraph',
    });
    sessionStorage.setItem('malpedia-pending-graph-context', text);
    navigate('/');
    toast.success('Graph context added to chat');
  }, [graphData.nodes, graph, navigate]);

  const isEmpty = graph.nodes.length === 0;

  const detailMessages = useMemo(
    () => messages.map(m => ({ id: m.id, role: m.role, content: m.content })),
    [messages],
  );

  return (
    <div className="malpedia-acc-subgraph-section">
      <div className="malpedia-acc-subgraph-header">
        <span className="malpedia-acc-subgraph-title">Investigation Subgraph</span>
        <span className="malpedia-acc-subgraph-count">
          {hasActiveFilters
            ? `${visibleNodes} / ${graph.nodes.length} nodes, ${visibleEdges} / ${graph.edges.length} edges`
            : `${graph.nodes.length} nodes, ${graph.edges.length} edges`}
        </span>
      </div>

      {isEmpty ? (
        <div className="malpedia-acc-subgraph-empty">
          {emptyMessage ?? 'Send a query in the chat to populate the investigation graph. Entities and documents from each retrieval will accumulate here across queries.'}
        </div>
      ) : (
        <div className="malpedia-acc-subgraph-content">
          <SubgraphToolbar
            graph={graph}
            onResetLayout={resetLayout}
            onClearUnpinned={onClearUnpinned}
            onClearAll={onClearAll}
            subgraphScope={subgraphScope}
            onSetScope={onSetScope}
            onCopyToClipboard={handleCopyGraphToClipboard}
            onInsertIntoChat={handleInsertGraphIntoChat}
            onExpand={onExpand}
            expandLabel={expandLabel}
            expandTitle={expandTitle}
            onSaveToGraphs={onSaveToGraphs}
          />
          <div className="malpedia-acc-graph-wrapper" ref={wrapperRef}>
            <div className="malpedia-acc-graph-canvas">
              <ForceGraph2D
                ref={fgRef as React.MutableRefObject<FGRef | undefined>}
                graphData={graphData}
                width={dimensions.width}
                height={dimensions.height}
                backgroundColor="#0a0a0f"
                nodeCanvasObjectMode={() => 'replace'}
                nodeCanvasObject={nodeCanvasObject}
                nodePointerAreaPaint={nodePointerAreaPaint}
                linkColor={linkColor}
                linkWidth={linkWidth}
                linkLineDash={linkLineDash}
                linkDirectionalParticles={3}
                linkDirectionalParticleWidth={2.0}
                linkDirectionalParticleSpeed={0.004}
                linkDirectionalParticleColor={linkParticleColor}
                linkHoverPrecision={4}
                onNodeClick={handleNodeClick}
                onNodeHover={onNodeHover}
                onNodeDragEnd={onNodeDragEnd}
                onLinkHover={onLinkHover}
                onNodeRightClick={(node, event) => {
                  event.preventDefault();
                  setContextMenu({ x: event.offsetX, y: event.offsetY, nodeId: node.id as string, isCanvas: false });
                }}
                onBackgroundClick={() => {
                  setSelectedNode(null);
                  setContextMenu(null);
                  handleNodeUnhover();
                  setTooltip(null);
                }}
                onBackgroundRightClick={(event) => {
                  event.preventDefault();
                  setContextMenu({ x: event.offsetX, y: event.offsetY, isCanvas: true });
                }}
                cooldownTicks={80}
                warmupTicks={30}
                enableNodeDrag={true}
                nodeLabel=""
              />
            </div>

            <GraphLegendPanel
              nodes={graph.nodes}
              edges={graph.edges}
              typeFilters={typeFilters}
              onToggleTypeFilter={handleToggleTypeFilter}
              onShowAll={handleShowAll}
              onHideAll={handleHideAll}
              edgeColorMode={edgeColorMode}
              onToggleEdgeColorMode={() => setEdgeColorMode(m => m === 'source' ? 'rel_type' : 'source')}
              iocSubTypeFilters={iocSubTypeFilters}
              onToggleIocSubType={handleToggleIocSubType}
              dateFrom={filterDateFrom}
              dateTo={filterDateTo}
              onDateRangeChange={handleDateRangeChange}
            />

            {tooltip && (
              <div className="malpedia-graph-tooltip" style={{ left: tooltip.x + 10, top: tooltip.y + 10 }}>
                <div><strong>{tooltip.name}</strong></div>
                <div style={{ opacity: 0.7, fontSize: '11px' }}>{tooltip.type}</div>
                {tooltip.strategy && (
                  <div style={{ opacity: 0.7, fontSize: '11px' }}>via {tooltip.strategy}</div>
                )}
                {tooltip.score != null && (
                  <div style={{ opacity: 0.7, fontSize: '11px' }}>score: {tooltip.score.toFixed(3)}</div>
                )}
                {tooltip.neighborCount != null && tooltip.neighborCount > 0 && (
                  <div style={{ opacity: 0.7, fontSize: '11px' }}>{tooltip.neighborSummary}</div>
                )}
              </div>
            )}

            {contextMenu && (
              <div className="malpedia-acc-context-menu" style={{ left: contextMenu.x, top: contextMenu.y }}>
                {contextMenu.nodeId && !contextMenu.isCanvas && (
                  <>
                    <button onClick={() => handleContextAction('pin')}>
                      {pinnedNode?.pinned ? 'Unpin' : 'Pin'}
                    </button>
                    <button onClick={() => handleContextAction('expand')}>
                      Expand Neighbors
                    </button>
                    <button onClick={() => handleContextAction('copy')}>
                      Copy Name
                    </button>
                    <button onClick={() => handleContextAction('inspect')}>
                      Inspect
                    </button>
                    {pinnedNode?.label === 'Document' && (
                      <button onClick={() => handleContextAction('go_to_document')}>
                        Go to Document
                      </button>
                    )}
                  </>
                )}
                {contextMenu.isCanvas && (
                  <>
                    <button onClick={() => handleContextAction('search_within')}>
                      Search Within Subgraph
                    </button>
                    <button onClick={() => handleContextAction('expand_from')}>
                      Expand From Subgraph
                    </button>
                    <button onClick={() => handleContextAction('reset_layout')}>
                      Reset Layout
                    </button>
                    <button onClick={() => handleContextAction('clear_unpinned')}>
                      Clear Unpinned
                    </button>
                  </>
                )}
              </div>
            )}

            {selectedNode && (
              <NodeDetailsPanel
                node={selectedNode}
                graph={graph}
                onClose={() => setSelectedNode(null)}
                onFocusNode={handleFocusNode}
                messages={detailMessages}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
